import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { NewReviTherapyApi } from "../../../../../api/revi";

const therapyApi = new NewReviTherapyApi();

// Los dos throw de este fichero son DIAGNOSTICO DE DESARROLLO, no interfaz:
// significan que falta un metodo en el cliente de la API. Van marcados con el
// prefijo [revi] para que quien los lea sepa que es un fallo de programacion y
// no un problema suyo, y por eso no pasan por el diccionario.
export function useSharedTherapies({ accessToken, onAfterClone } = {}) {
  const { t } = useTranslation("therapist");
  const [sharedOpen, setSharedOpen] = useState(false);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [sharedTherapies, setSharedTherapies] = useState([]);
  const [sharedPage, setSharedPage] = useState(1);
  const [sharedTotal, setSharedTotal] = useState(0);
  const [sharedSearch, setSharedSearch] = useState("");
  const sharedPageSize = 12;

  const fetchSharedTherapies = useCallback(async () => {
    if (!accessToken || !sharedOpen) return;

    // Guardia de desarrollo: nombra un metodo del cliente de API, no es texto
    // de interfaz y por eso no se traduce.
    if (typeof therapyApi.getSharedTherapies !== "function") {
      message.error(
        "[revi] NewReviTherapyApi: falta el metodo getSharedTherapies",
      );
      return;
    }

    setSharedLoading(true);
    try {
      const res = await therapyApi.getSharedTherapies(accessToken, {
        page: sharedPage,
        limit: sharedPageSize,
        search: sharedSearch,
      });

      const list =
        res?.therapies ||
        res?.data?.therapies ||
        (Array.isArray(res) ? res : []);

      const pagination = res?.pagination || res?.data?.pagination || null;

      setSharedTherapies(Array.isArray(list) ? list : []);
      setSharedTotal(
        pagination?.total ||
          pagination?.totalDocs ||
          pagination?.totalItems ||
          (Array.isArray(list) ? list.length : 0),
      );
    } catch (e) {
      console.error(e);
      message.error(t("salas.avisos.errorCargarCompartidas"));
      setSharedTherapies([]);
      setSharedTotal(0);
    } finally {
      setSharedLoading(false);
    }
  }, [accessToken, sharedOpen, sharedPage, sharedPageSize, sharedSearch, t]);

  useEffect(() => {
    fetchSharedTherapies();
  }, [fetchSharedTherapies]);

  const openSharedModal = useCallback(() => setSharedOpen(true), []);
  const closeSharedModal = useCallback(() => {
    setSharedOpen(false);
    setSharedSearch("");
    setSharedPage(1);
  }, []);

  const handleSharedSearch = useCallback((value) => {
    setSharedSearch(value || "");
    setSharedPage(1);
  }, []);

  const handleSharedPageChange = useCallback((p) => setSharedPage(p), []);

  const handleCloneSharedTherapy = useCallback(
    async (therapy) => {
      if (!accessToken || !therapy?._id) return;

      // Guardia de desarrollo, igual que la anterior: no es texto de interfaz.
      if (typeof therapyApi.cloneTherapy !== "function") {
        message.error("[revi] NewReviTherapyApi: falta el metodo cloneTherapy");
        return;
      }

      try {
        await therapyApi.cloneTherapy(accessToken, therapy._id);
        message.success(t("salas.avisos.terapiaClonada"));
        onAfterClone?.();
        fetchSharedTherapies();
      } catch (e) {
        console.error(e);
        message.error(t("salas.avisos.errorClonarTerapia"));
      }
    },
    [accessToken, onAfterClone, fetchSharedTherapies, t],
  );

  return {
    sharedOpen,
    sharedLoading,
    sharedTherapies,
    sharedPage,
    sharedPageSize,
    sharedTotal,

    openSharedModal,
    closeSharedModal,
    handleSharedSearch,
    handleSharedPageChange,
    handleCloneSharedTherapy,
  };
}
